import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, User, LogOut, Shield, LogIn, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const primaryLinks = [
  { to: "/events", label: "Events" },
  { to: "/leaderboards", label: "Leaderboards" },
  { to: "/results", label: "Results" },
  { to: "/where-to-play", label: "Where to Play" },
  { to: "/gallery", label: "Gallery" },
  { to: "/become-a-legend", label: "Become a Legend" },
  { to: "/become-host", label: "Become a Host Venue" },
];

const moreLinks = [
  { to: "/about", label: "About" },
  { to: "/hall-of-legends", label: "Hall of Legends" },
  { to: "/seasons", label: "Season Archive" },
  { to: "/member-charter", label: "Member Charter" },
  { to: "/rules", label: "Tournament Rules" },
  { to: "/points-payouts", label: "Points / Payouts / Structures" },
  { to: "/responsible-gaming", label: "Responsible Gaming" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const go = (to) => { setAuthOpen(false); setOpen(false); navigate(to); };
  const isActive = (to) => location.pathname === to;

  return (
    <nav className="fixed w-full z-50 glass border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img src="/legends-logo.png?v=4" alt="Legends Poker" className="h-12 w-12 object-contain group-hover:scale-105 transition-transform" />
            <div className="leading-none hidden sm:block">
              <div className="font-display text-xl font-bold tracking-tight text-white">LEGENDS</div>
              <div className="text-[10px] tracking-[0.35em] text-gold uppercase">Poker League</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {primaryLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive(l.to) ? "text-gold" : "text-slate-300 hover:text-gold"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
              <button className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-gold flex items-center gap-1 transition-colors" data-testid="nav-more">
                More <ChevronDown className="w-4 h-4" />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full w-64 glass border border-white/10 py-2 shadow-2xl">
                  {moreLinks.map((l) => (
                    <Link key={l.to} to={l.to} className="block px-4 py-2.5 text-sm text-slate-300 hover:text-gold hover:bg-white/5 transition-colors"
                      data-testid={`more-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin">
                    <Button variant="outline" className="border-gold/40 text-gold hover:bg-gold hover:text-navy rounded-none text-xs uppercase tracking-widest">
                      <Shield className="w-4 h-4 mr-1" /> Admin
                    </Button>
                  </Link>
                )}
                <Link to="/dashboard" data-testid="nav-dashboard">
                  <Button variant="ghost" className="text-slate-200 hover:text-gold rounded-none">
                    <User className="w-4 h-4 mr-1" /> {user.name?.split(" ")[0] || "Me"}
                  </Button>
                </Link>
                <Button onClick={handleLogout} variant="ghost" size="icon" className="text-slate-400 hover:text-gold rounded-none" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button onClick={() => setAuthOpen(true)} className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs px-6" data-testid="nav-register-login">
                Register / Log In
              </Button>
            )}
          </div>

          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-white/10 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col">
            {[...primaryLinks, ...moreLinks].map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className="py-3 text-slate-200 hover:text-gold border-b border-white/5 text-sm">
                {l.label}
              </Link>
            ))}
            <div className="pt-4 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)}><Button className="w-full rounded-none bg-secondary">My Dashboard</Button></Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)}><Button className="w-full rounded-none bg-gold text-navy">Admin</Button></Link>}
                  <Button onClick={() => { handleLogout(); setOpen(false); }} variant="outline" className="w-full rounded-none">Log Out</Button>
                </>
              ) : (
                <Button onClick={() => setAuthOpen(true)} className="w-full rounded-none bg-gold text-navy font-bold" data-testid="mobile-register-login">Register / Log In</Button>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent className="bg-navy-paper border-slate-800 rounded-none max-w-md" data-testid="auth-choice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">Welcome to Legends</DialogTitle>
            <DialogDescription className="text-slate-400">Already a member, or ready to join the League?</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 pt-2">
            <Button onClick={() => go("/login")} className="w-full bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest py-6" data-testid="auth-choice-login">
              <LogIn className="w-4 h-4 mr-2" /> Log In
            </Button>
            <Button onClick={() => go("/become-a-legend")} variant="outline" className="w-full border-gold/40 text-gold hover:bg-gold hover:text-navy rounded-none font-bold uppercase tracking-widest py-6" data-testid="auth-choice-become-legend">
              <Star className="w-4 h-4 mr-2" /> Become a Legend
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </nav>
  );
}
